import { GCInstant } from 'src/lib/gcinstant';
import * as Native from 'src/replicant/native';
import StateObserver, { user, now } from 'src/StateObserver';
import features from 'src/replicant/ruleset/features';
import { assertNever } from 'src/replicant/utils';
import { captureMessage } from '@sentry/browser';
import { withLoading } from '../../lib/utils';
import { EntryProperties } from 'src/lib/analytics/properties';
import { versionUpdateConfig } from 'src/replicant/native/native.ruleset';
import userAgent from '@play-co/user-agent';
import { compareVersions, isValidVersion } from 'src/replicant/utils/string';
import { Feature } from 'src/replicant/analytics';
import { guessIsNativeAppInstalled as guessIsNativeAppInstalledGetter } from 'src/replicant/native/native.getters';
import { trackNativeCrossplayStart } from '../crossplay/crossplay.tracking';
import type { CrossPlayStartParams } from '../crossplay/crossplay.types';
import * as Promo from 'src/replicant/promo';

export function isNativePromoAvailable() {
  return isNativeAppAvailable();
}

export const isNativeAppAvailable = () => {
  const { user } = StateObserver.getState();

  switch (GCInstant.osType) {
    case 'IOS':
      return (
        features(user).nativeIos &&
        compareVersions(userAgent.os.version, '16.0') >= 0
      );
    case 'ANDROID':
      return (
        features(user).nativeAndroidCrossplayPromo &&
        compareVersions(userAgent.os.version, '7.1') >= 0
      );
    case 'WEB_IOS': // Only for development
    case 'WEB_ANDROID':
      // Note: open web test (0301)
      return !!process.env.IS_DEVELOPMENT;
    default:
      return false;
  }
};

/**
 * Check if the current platform is a supported mobile platform. Currently, only IOS and ANDROID are supported.
 * @returns {boolean} True if the platform is supported, false otherwise.
 */
export function isSupportedMobilePlatform() {
  const osType = GCInstant.osType;
  switch (osType) {
    case 'IOS':
    case 'ANDROID':
    case 'WEB_IOS':
    case 'WEB_ANDROID':
    case 'ANDROID_APP':
    case 'IOS_APP':
      return true;
    case 'MOBILE_WEB':
    case 'DESKTOP':
    case 'WEB':
    case undefined:
      return false;
    default:
      try {
        assertNever(osType);
      } catch (e) {
        captureMessage('Unknown osType', {
          level: 'warning',
          extra: { osType },
        });
      }
      return false;
  }
}

/**
 * The word GUESS is used here for a purpose: we can't be 100% sure that a user has the native app,
 * but we're trying to "predict" it:
 * - check if replicant has data about native app installed,
 * - check if the native app was launched recently
 */
export function guessIsNativeAppInstalled(platform: 'IOS' | 'ANDROID') {
  return guessIsNativeAppInstalledGetter(
    StateObserver.getState().user,
    platform,
    StateObserver.now(),
  );
}

/**
 * The word GUESS is used here for a purpose: we can't be 100% sure that a user has the native app,
 * but we're trying to "predict" it:
 * - check if replicant has data about native app installed,
 * - check if the native app was launched recently
 */
export function guessInstalledNativeAppPlatform():
  | 'ANDROID_APP'
  | 'IOS_APP'
  | null {
  // Is IOS app installed
  const iosInstalled =
    GCInstant.osType === 'IOS' && guessIsNativeAppInstalled('IOS');
  if (iosInstalled) {
    return 'IOS_APP';
  }

  // Is Android app installed
  const androidInstalled =
    GCInstant.osType === 'ANDROID' && guessIsNativeAppInstalled('ANDROID');
  if (androidInstalled) {
    return 'ANDROID_APP';
  }

  return null;
}

export function hasLaunchedNativeAppPlatform() {
  const { native } = StateObserver.getState().user;
  return native.appleLastLaunched > 0 || native.googleLastLaunched > 0;
}

/**
 * Open app store depend on osType
 */
export async function openNativeAppStorePage(opts: {
  payload?: Partial<EntryProperties>;
  tracking: CrossPlayStartParams;
}) {
  // Tracking
  const tracking = {
    ...opts.tracking,
    target: 'native',
  } as const;

  trackNativeCrossplayStart(tracking, true);

  // Determine deep link with payload
  const switchPayload = {
    ...(opts.payload || {}),
    ...GCInstant.crossplay.getCrossplayPayload({
      ...tracking,
      triggerSource: `${tracking.origin}_install`,
      wasInBackground: false,
      isInferred: true,
    }),
  };
  // Save payload for session resume when user installs the app.
  await withLoading(
    async () => {
      await StateObserver.invoke.setPersistedPayload(
        JSON.stringify(switchPayload),
      );
      await StateObserver.flush();
    },
    {
      feature: Feature.NATIVE._,
      subFeature: Feature.NATIVE.SET_PERSISTED_PAYLOAD,
    },
  );

  switch (GCInstant.osType) {
    case 'IOS':
    case 'IOS_APP':
    case 'WEB_IOS':
      window.location.assign(Native.ruleset.appStoreURL);
      break;

    case 'ANDROID':
    case 'ANDROID_APP':
    case 'WEB_ANDROID':
      window.open(Native.ruleset.googlePlayURL);
      break;

    default:
      captureMessage('Unsupported app store platform', {
        level: 'warning',
        extra: { platform: GCInstant.osType },
      });
  }
}

export function getNativeAppUpdatePromptType(): 'hard' | 'soft' | 'skip' {
  const legacyNative = GCInstant.legacyNative;

  // Not in native client
  if (!legacyNative) return 'skip';

  const version = legacyNative.app.version;
  const os = legacyNative.platform.os;
  const updateConfig = versionUpdateConfig[os];

  // Unknown OS or no config
  if (!updateConfig) return 'skip';

  // If allowedVersions is configured, use strict string comparison
  if (updateConfig.allowedVersions?.length) {
    return version && updateConfig.allowedVersions.includes(version)
      ? 'skip'
      : 'hard';
  }

  // No thresholds configured
  if (!updateConfig.hard && !updateConfig.soft) return 'skip';

  // Missing or invalid version - require force update if hard threshold exists
  if (!version || !isValidVersion(version)) {
    return updateConfig.hard ? 'hard' : 'skip';
  }

  if (updateConfig.hard && compareVersions(version, updateConfig.hard) < 0) {
    return 'hard';
  }

  if (updateConfig.soft && compareVersions(version, updateConfig.soft) < 0) {
    return 'soft';
  }

  return 'skip';
}

/**
 * Determine if we should remind user to update native app
 */
export function shouldRemindNativeAppUpdate() {
  if (getNativeAppUpdatePromptType() === 'soft') {
    // Find how many times we have shown the reminder
    const timesShown = Promo.getters.getPromoShownCounter(
      user(),
      'updateAppReminder24h',
    );

    // For the first 3 times, use the 24h promo, reminding every day
    if (
      timesShown < 3 &&
      Promo.getters.isPromoReady(user(), 'updateAppReminder24h', now())
    ) {
      return true;
    }

    // After the 3rd time, use the 72h promo, reminding less often
    if (Promo.getters.isPromoReady(user(), 'updateAppReminder72h', now())) {
      return true;
    }
  }

  // Always remind for hard update
  return getNativeAppUpdatePromptType() === 'hard';
}
